import { useState, useEffect } from "react";
import "./ViewPage.css";
import "./DeleteOrders.css";
import { FiTrash2 } from "react-icons/fi";
import { authFetch } from "./authFetch";

const PAGE_SIZE = 10;
const MAX_CLIENT_RESULTS = 1000;

const STATUS_COLORS = {
  "In-Process": "#99dff7ff",
  Pending: "#f3c574ff",
  Shipped: "#e0dd3eff",
  "Out for Delivery": "#cbb8ebff",
  Delivered: "#86efac",
  Cancelled: "#fca5a5ff",
};

const LETTERS = [
  "#","A","B","C","D","E","F","G","H","I","J","K","L",
  "M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z"
];

export default function DeleteOrders() {
  const [clinic, setClinic] = useState("");
  const [patient, setPatient] = useState("");
  const [rawResults, setRawResults] = useState([]);

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("NEWEST");
  const [physicianFilter, setPhysicianFilter] = useState("ALL"); // F-04
  const [currentPage, setCurrentPage] = useState(1);

  const [openTracking, setOpenTracking] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // ============================
  // DELETE MODAL STATES
  // ============================
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ============================
  // ALPHABET MODAL STATE
  // ============================
  const [allClinics, setAllClinics] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [letterClinics, setLetterClinics] = useState([]);

  const display = (v) => (!v || v.trim() === "" ? "N/A" : v);
  // F-11: strip timestamp from date fields
  const displayDate = (v) => {
    if (!v || v.trim() === "") return "N/A";
    return v.split(" ")[0];
  };

  // ============================
  // LOAD CLINICS
  // ============================
 useEffect(() => {
  async function loadClinics() {
    try {
      const res = await authFetch(`${API_BASE}/admin/clinics`);
      const data = await res.json();

      const names = (data.items || []).map(i => i.clinicName);
      names.sort((a, b) => a.localeCompare(b));
      setAllClinics(names);
    } catch (err) {
      console.error("Failed to load clinics", err);
    }
  }

  loadClinics();
}, [API_BASE]);


  // ============================
  // ALPHABET CLICK
  // ============================
  function handleLetterClick(letter) {
    setSelectedLetter(letter);

    let list = [];

    if (letter === "#") {
      list = allClinics.filter(c => !/^[A-Za-z]/.test(c));
    } else {
      list = allClinics.filter(c =>
        c.toUpperCase().startsWith(letter)
      );
    }

    setLetterClinics(list);
    setModalOpen(true);
  }

  // ============================
  // ADMIN SEARCH (REAL ORDER ID)
  // ============================
 async function search() {
  if (clinic.trim().length < 3) {
    setError("Clinic name must be at least 3 characters.");
    return;
  }

  setError("");
  setLoading(true);
  setHasSearched(false);
  setOpenTracking({});
  setCurrentPage(1);
  // F-04: reset filters on every new search so stale selections don't hide results
  setStatusFilter("ALL");
  setPhysicianFilter("ALL");

  try {
    const params = new URLSearchParams();
    params.append("clinic", clinic.trim());
    if (patient.trim()) params.append("patient", patient.trim());

    const res = await authFetch(`${API_BASE}/admin/orders?${params.toString()}`);


    if (!res.ok) throw new Error();

    const data = await res.json();
    const results = (data.results || []).slice(0, MAX_CLIENT_RESULTS);

    setRawResults(results);
    setHasSearched(true);

  } catch {
    setError("Unable to fetch orders.");
    setRawResults([]);
    setHasSearched(true);
  } finally {
    setLoading(false);
  }
}

  // ============================
  // FILTERING + SORTING
  // ============================
  let filtered = rawResults;

  if (patient.trim()) {
    const p = patient.toLowerCase();
    filtered = filtered.filter(o => o.name?.toLowerCase().includes(p));
  }

  if (statusFilter !== "ALL") {
    filtered = filtered.filter(o => o.status === statusFilter);
  }

  // F-04: physician filter
  if (physicianFilter !== "ALL") {
    filtered = filtered.filter(o => o.physician === physicianFilter);
  }

  filtered = [...filtered].sort((a, b) => {
    const da = new Date(a.order_received || 0); // F-02/03: order_received
    const db = new Date(b.order_received || 0); // F-02/03: order_received
    return sortOrder === "NEWEST" ? db - da : da - db;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [patient, statusFilter, sortOrder, physicianFilter]); // F-04

  // ============================
  // DELETE HANDLERS
  // ============================
  function openDeleteModal(order) {
    setOrderToDelete(order);
    setDeleteModalOpen(true);
  }

  function closeDeleteModal() {
    if (!deleting) {
      setDeleteModalOpen(false);
      setOrderToDelete(null);
    }
  }

  async function confirmDelete() {
    if (!orderToDelete) return;

    setDeleting(true);

    try {
      const res = await authFetch(
        `${API_BASE}/admin/delete-order/${orderToDelete.orderId}`,
        { method: "DELETE" }
      );

      const data = await res.json();

      if (!res.ok || !data.deleted) throw new Error();

      setRawResults(prev =>
        prev.filter(o => o.orderId !== orderToDelete.orderId)
      );

    } catch {
      alert("Failed to delete order.");
    } finally {
      setDeleting(false);
      closeDeleteModal();
    }
  }

  return (
    <div className="view-wrapper">

      {/* DELETE MODAL */}
      {deleteModalOpen && (
        <div className="modal-overlay">
          <div className="clinic-modal">

            <div className="modal-header">
              <span>Delete Order</span>
              <button className="modal-close-btn" onClick={closeDeleteModal}>
                ×
              </button>
            </div>

            <p style={{ fontSize: "15px", color: "#374151" }}>
              Delete <strong>{orderToDelete?.name}</strong>?
            </p>

            <div className="delete-modal-footer">
              <button
                className="delete-confirm-btn"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Yes, Delete"}
              </button>

              <button
                className="delete-cancel-btn"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* CLINIC LETTER MODAL */}
      {modalOpen && (
        <div className="modal-overlay">
          <div className="clinic-modal">

            <div className="modal-header">
              <span>Clinics starting with "{selectedLetter}"</span>
              <button className="modal-close-btn" onClick={() => setModalOpen(false)}>
                ×
              </button>
            </div>

            <div className="clinic-modal-scroll">
              {letterClinics.length === 0 ? (
                <div className="modal-empty">No clinics found</div>
              ) : (
                letterClinics.map((name, i) => (
                  <div
                    key={i}
                    className="clinic-modal-item"
                    onClick={() => {
                      setClinic(name);
                      setModalOpen(false);
                      search();
                    }}
                  >
                    {name}
                  </div>
                ))
              )}
            </div>

            <div className="modal-footer">
              <button className="modal-close" onClick={() => setModalOpen(false)}>
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      <h2 className="admin-page-title">Delete Orders</h2>

      {/* SEARCH CARD */}
      <div className="search-card-admin">
        {/* ALPHABET BAR */}
        <div className="alphabet-wrapper">
          <div className="alphabet-bar alphabet-inside">
            {LETTERS.map((letter) => (
              <button
                key={letter}
                className="letter-btn"
                onClick={() => handleLetterClick(letter)}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        {/* SEARCH FIELDS */}
        <div className="admin-search-grid">
          <input
            placeholder="Clinic name"
            value={clinic}
            onChange={(e) => setClinic(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
          <input
            placeholder="Patient name (optional)"
            value={patient}
            onChange={(e) => setPatient(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
          <button onClick={search} disabled={loading}>
            {loading ? "Searching…" : "Search"}
          </button>
        </div>

        {/* FILTERS */}
        <div className="filters-admin">
          <div>
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option>ALL</option>
              <option>In-Process</option>
              <option>Pending</option>
              <option>Shipped</option>
              <option>Out for Delivery</option>
              <option>Delivered</option>
              <option>Cancelled</option>
            </select>
          </div>

          <div>
            <label>Sort</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="NEWEST">Newest first</option>
              <option value="OLDEST">Oldest first</option>
            </select>
          </div>

          {/* F-04: Physician dropdown - populated from current results */}
          <div>
            <label>Physician</label>
            <select
              value={physicianFilter}
              onChange={(e) => setPhysicianFilter(e.target.value)}
            >
              <option value="ALL">All Physicians</option>
              {Array.from(
                new Set(
                  rawResults
                    .map((o) => o.physician)
                    .filter((p) => p && p.trim() !== "")
                )
              )
                .sort((a, b) => a.localeCompare(b))
                .map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
            </select>
          </div>
        </div>

        {error && <p className="error-admin">{error}</p>}
      </div>

      {/* NO RESULTS */}
      {hasSearched && !loading && filtered.length === 0 && !error && (
        <p className="no-results">No orders found.</p>
      )}

      {/* RESULTS GRID */}
      <div className="results-grid-admin">
        {paginated.map((o, i) => (
          <div key={i} className="result-card-admin">

            <div className="rc-top">
              <div className="rc-name">{o.name}</div>

              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <span
                  className="rc-status"
                  style={{ background: STATUS_COLORS[o.status] }}
                >
                  {o.status}
                </span>

                <FiTrash2
                  className="delete-icon"
                  onClick={() => openDeleteModal(o)}
                />
              </div>
            </div>

            <div className="rc-dates">
              {/* F-02/03: <strong>Shipped:</strong> {display(o.shipDate)} */}
                <strong>Order Received:</strong> {displayDate(o.order_received)}
              {/* F-12: Delivery field removed - <strong>Delivery:</strong> {display(o.deliveryDate)} */}
            </div>

            {/* F-04: Physician display - using rc-notes class for consistent styling */}
            <div className="rc-notes">
              <strong>Physician:</strong> {display(o.physician)}
            </div>

            <div className="rc-notes">
              <strong>Notes:</strong> {display(o.comments)}
            </div>

            {/* Tracking */}
            {o.trackingLinks?.length === 1 && (
              <a
                href={o.trackingLinks[0]}
                target="_blank"
                rel="noreferrer"
                className="track-admin"
              >
                Track
              </a>
            )}

            {o.trackingLinks?.length > 1 && (
              <>
                <button
                  className="track-toggle-admin"
                  onClick={() =>
                    setOpenTracking((prev) => ({
                      ...prev,
                      [i]: !prev[i],
                    }))
                  }
                >
                  Tracking ({o.trackingLinks.length})
                  <span className={`rc-arrow ${openTracking[i] ? "open" : ""}`}>
                    ▸
                  </span>
                </button>

                {openTracking[i] && (
                  <div className="rc-track-list">
                    {o.trackingLinks.map((link, idx) => (
                      <a
                        key={idx}
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        className="track-admin small"
                      >
                        Track {idx + 1}
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}

          </div>
        ))}
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="admin-pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
            First
          </button>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Prev
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </button>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            Last
          </button>
        </div>
      )}

    </div>
  );
}
